/** Pure touch math, kept apart from Phaser so it can be tested. Distances are game pixels. */

/** How far a thumb must travel, mostly vertically, to count as a lane swipe. */
export const SWIPE_MIN = 16;
/** A swipe slower than this is a drag, not a swipe. */
export const SWIPE_MAX_MS = 450;

/**
 * Reads a lane swipe from a thumb's travel. Up means one lane farther back (+1), down one lane
 * nearer (-1), anything else is no swipe (0).
 */
export function classifySwipe(dx: number, dy: number, elapsedMs: number): -1 | 0 | 1 {
  if (elapsedMs > SWIPE_MAX_MS) return 0;
  if (Math.abs(dy) < SWIPE_MIN) return 0;
  if (Math.abs(dy) < Math.abs(dx) * 1.3) return 0;
  return dy < 0 ? 1 : -1;
}

/** Floating stick: the base appears under the thumb and follows it if dragged past the rim. */
export const STICK_RADIUS = 22;
/** Travel below this fraction of the radius is ignored (resting thumbs drift). */
export const STICK_DEADZONE = 0.22;

/**
 * Horizontal stick value in [-1, 1] for a thumb `dx` pixels from the stick's centre. Past the
 * dead zone the value rises linearly to full speed at the rim.
 */
export function stickValue(dx: number): number {
  const t = Math.min(1, Math.abs(dx) / STICK_RADIUS);
  if (t < STICK_DEADZONE) return 0;
  return (Math.sign(dx) * (t - STICK_DEADZONE)) / (1 - STICK_DEADZONE);
}

/** Where the stick's centre must move so the thumb at (x, y) stays on its rim. */
export function dragStick(
  cx: number,
  cy: number,
  x: number,
  y: number,
): { cx: number; cy: number } {
  const dx = x - cx;
  const dy = y - cy;
  const dist = Math.hypot(dx, dy);
  if (dist <= STICK_RADIUS) return { cx, cy };
  const k = (dist - STICK_RADIUS) / dist;
  return { cx: cx + dx * k, cy: cy + dy * k };
}

/** Stick travel that counts as a flick, and how far back toward centre ends it (halfway). */
export const FLICK_ON = 0.8;
export const FLICK_OFF = 0.5;
/** A push held at the rim longer than this is running, not a flick. */
export const FLICK_HOLD_MS = 500;
/** After the first flick eases off, the second must reach the rim within this. */
export const DOUBLE_FLICK_GAP_MS = 550;

type Dir = -1 | 1;

interface Push {
  dir: Dir;
  at: number;
  /** The thumb swinging back after a flick, not a push of its own. */
  swing: boolean;
  dashed: boolean;
}

/**
 * Detects a double flick of the stick (the touch dash): push to the rim, ease back (or lift the
 * thumb), push the same way again. The window is timed from the first flick's release, so slow,
 * long flicks count as well as snappy ones.
 *
 * A long flick drags the floating stick's centre along, so swinging the thumb back to where it
 * started reads as a push the other way. Any opposite push within the window after a flick (or a
 * dash) is that swing and is ignored, so it neither cancels the flick nor starts a dash back.
 */
export class FlickDetector {
  private push: Push | null = null;
  /** The last push that was not a swing: which way, when it ended, and whether it was short. */
  private last: { dir: Dir; endedAt: number; short: boolean; dashed: boolean } | null = null;

  /** Feed every stick value (0 when the thumb lifts). True on the flick that completes a double. */
  update(value: number, nowMs: number): boolean {
    const dir: Dir = value > 0 ? 1 : -1;
    const mag = Math.abs(value);
    if (this.push) {
      if (mag > FLICK_OFF && dir === this.push.dir) return false;
      this.end(this.push, nowMs);
      this.push = null;
    }
    if (mag < FLICK_ON) return false;
    const last = this.last;
    const recent = last !== null && last.short && nowMs - last.endedAt <= DOUBLE_FLICK_GAP_MS;
    const swing = recent && last.dir !== dir;
    const double = recent && last.dir === dir && !last.dashed;
    this.push = { dir, at: nowMs, swing, dashed: double };
    return double;
  }

  private end(push: Push, nowMs: number): void {
    if (push.swing) return;
    const short = nowMs - push.at <= FLICK_HOLD_MS;
    this.last = { dir: push.dir, endedAt: nowMs, short, dashed: push.dashed };
  }
}
